<template>
    <Card :title="$t('technicalMonitoringAdvice')" class="mt-3">
        <p class="mb-6 flex flex-row">
            <span v-show="isCloudChartLoading || reportService.cloudTopRank.length > 0" class="mr-6 inline-block w-1/2 text-sm leading-5 text-black">{{ cloudTitle }}</span>
            <span v-show="isHeatMapLoading || reportService.heatTopRank.length > 0" class="inline-block w-1/2 text-sm leading-5 text-black">{{ heatmapTitle }}</span>
        </p>
        <div class="flex flex-row">
            <div class="mr-6 h-[225px] w-1/2 border border-grey-40">
                <BIChart
                    :chartKey="charts[0].chartType"
                    :isLoading="isCloudChartLoading"
                    :configTaskFunc="configTaskFunc(charts[0].config)"
                    :colorFunc="colorFunc(charts[0].chartType)"
                    :dataTaskFunc="dataTaskFunc(charts[0].chartType)"
                    :getChartStr="(data) => getChartImageStr(charts[0].chartType, data)"
                    class="h-full w-full"
                />
            </div>
            <div class="h-[225px] w-1/2 border border-grey-40">
                <BIChart
                    :chartKey="charts[1].chartType"
                    :isLoading="isHeatMapLoading"
                    :configTaskFunc="configTaskFunc(charts[1].config)"
                    :colorFunc="colorFunc(charts[1].chartType)"
                    :dataTaskFunc="dataTaskFunc(charts[1].chartType)"
                    :getChartStr="(data) => getChartImageStr(charts[1].chartType, data)"
                    class="h-full w-full"
                />
            </div>
        </div>
    </Card>
</template>

<script setup lang="ts">
import { watch, ref } from 'vue';
import Card from '@/components/Card.vue';
import BIChart from '@/components/analysis/BIChart.vue';
import { useSuggestionCard } from '@/composables/use-suggestion-card';
import { reportService } from '@/services/report-service'
import { E_ANALYSIS_CHART } from '@/components/analysis/charts';
import _ from 'lodash';

const props = defineProps<{
    companyName: string;
}>();

const emits = defineEmits<{(e: 'update:Analysis', data: { type: string, img: string, topRank: string[], name: string }): void}>();
const isHeatMapLoaded = ref(false);
const isCloudChartLoaded = ref(false);

const {
    charts,
    loadFunc,
    isHeatMapLoading,
    isCloudChartLoading,
    dataTaskFunc,
    configTaskFunc,
    colorFunc,
    cloudTopRank,
    heatmapTopRank,
    cloudTitle,
    heatmapTitle,
} = useSuggestionCard(props);

watch(() => [isCloudChartLoaded.value, isHeatMapLoaded.value], () => {
    reportService.value.isChartLoaded = isCloudChartLoaded.value && isHeatMapLoaded.value;
});

watch(() => props.companyName, async(val) => {
    if (val) {
        Promise.all([loadFunc(charts[0].chartType)(val), loadFunc(charts[1].chartType)(val)]);
    }
}, { immediate: true });

function getChartImageStr(chartType: E_ANALYSIS_CHART, data: { img: string, data: { name: string }[][] }) {
    if (chartType === E_ANALYSIS_CHART.TECH_DOMAIN_WORD_CLOUD) {
        cloudTopRank.value = _.get(data, 'data[0]', []).map(item => item?.name || '');
        isCloudChartLoaded.value = true;
        emits('update:Analysis', {
            type: 'cloud',
            img: data.img,
            topRank: cloudTopRank.value,
            name: props.companyName,
        });
    } else {
        const list = _.reverse(_.sortBy(_.get(data, 'data[1]', []), ['value', 'name'])) as { name: string, value: number }[];
        heatmapTopRank.value = list.map(item => item?.name || '');
        isHeatMapLoaded.value = true;
        emits('update:Analysis', {
            type: 'heatmap',
            img: data.img,
            topRank: heatmapTopRank.value,
            name: props.companyName,
        });
    }
}
</script>
